import type {
	IGridifyPaging,
	IMessage,
	IMessageRoom as IMessageRoomResponse,
	IRenterInternalNote,
	IResponseBase,
} from '@/interfaces';
import { MessageOrigin } from '@/interfaces/IMessage';
import { Http, HttpService } from './http';
import type { TEntityTypes } from '@/features/EntityTags/EntityTagsEntities';
import type { IGridifyQuery } from '@/models/GridifyQuery';
import type { ITwilioInboxMessage, TwilioMessagePeriod } from '@/views/messages/TwilioInbox.types';
import type {
	CollectionCall,
	CollectionCallTask,
} from '@/features/Cases/Collections/Types/CollectionTypes';

const http = Http.create();

export class MessageService extends HttpService {
	public static async getMessageHistory(
		entityId: string,
		entityType: string,
		query: Partial<IGridifyQuery>,
	) {
		const response = await http.get<IResponseBase<any>>(`messages/${entityType}/${entityId}/history`, {
			params: query,
		});
		return response.data;
	}

	/**
	 * Send message based on entity id and type
	 * @param text
	 * @param entityId
	 * @param entityType
	 * @returns
	 */
	public static async sendMessage(
		text: string,
		entityId: string,
		entityType: string,
		origin: MessageOrigin,
		isInternal = false,
	) {
		const response = await http.post<IResponseBase<boolean>>(`messages/${entityType}/${entityId}`, {
			text,
			origin,
			isInternal,
		});
		return response.data;
	}

	/**
	 * Get messages based on entity id and type
	 * @param entityId
	 * @param entityType
	 * @returns
	 */
	public static async getMessages(entityId: string, entityType: string) {
		const response = await http.get<IResponseBase<IMessage[]>>(
			`messages/${entityType}/${entityId}`,
		);
		return response.data;
	}

	public static async getMessageRoom(
		entityId: string,
		entityType: TEntityTypes,
		useCancelToken = true,
	) {
		const cancelToken = this.createCancelToken('getMessageRoom');

		const response = await http.get<IResponseBase<IMessageRoomResponse>>(
			`message-rooms/${entityType}/${entityId}`,
			useCancelToken ? { cancelToken } : {},
		);
		return response.data;
	}

	public static async getMessageRooms(entityType: TEntityTypes) {
		const response = await http.get<IResponseBase<IMessageRoomResponse[]>>(
			`message-rooms/${entityType}`,
		);
		return response.data;
	}

	public static async readRoom(entityId: string, entityType: TEntityTypes) {
		const response = await http.patch(`message-rooms/${entityType}/${entityId}/read`);
		return response.data;
	}

	/**
	 * Lê as mensagens recebidas no número do Twilio (inbox), filtradas por período.
	 * @param period janela de tempo (`dateSentAfter`)
	 * @param limit quantidade máxima de mensagens
	 */
	public static async getTwilioInboxMessages(period: TwilioMessagePeriod) {
		const response = await http.get<IResponseBase<ITwilioInboxMessage[]>>(`messages/twilio/inbox`, {
			params: { period },
		});
		return response.data;
	}

	/**
	 * Get the internal notes (diário de bordo) for a renter.
	 */
	public static async getRenterInternalNotes(renterId: string, query?: Partial<IGridifyQuery>) {
		const response = await http.get<IResponseBase<IGridifyPaging<IRenterInternalNote>>>(
			`messages/renters/${renterId}/internal-notes`,
			{ params: query },
		);
		return response.data.data;
	}

	/**
	 * Add an internal note (diário de bordo) to a renter.
	 */
	public static async addRenterInternalNote(renterId: string, text: string) {
		const response = await http.post<IResponseBase<IRenterInternalNote>>(
			`messages/renters/${renterId}/internal-notes`,
			{ text },
		);
		return response.data.data;
	}

	public static async syncLastWahaMessages(entityId: string, entityType: TEntityTypes) {
		const response = await http.get(`tools/sync-waha-chat-messages`, {
			params: {
				entityId,
				entityType,
			},
		});
		return response.data;
	}

	/**
	 * Fila de ligações manuais de cobrança (persistidas como Messages com
	 * MessageOrigin.ManualCall). Retorna a página de ligações a fazer/feitas.
	 */
	public static async getCollectionCalls(query: IGridifyQuery) {
		const response = await http.get<IGridifyPaging<CollectionCallTask>>(
			'/cases/collections/calls',
			{ params: query },
		);
		return response.data;
	}

	/**
	 * Marca/desmarca uma ligação de cobrança (toggle bidirecional Pending <-> Sent).
	 * Só ADMIN pode chamar (endpoint gated).
	 */
	public static async toggleCollectionCall(callId: string) {
		const response = await http.post<IResponseBase<CollectionCall>>(
			`/cases/collections/calls/${callId}/toggle`,
		);
		return response.data;
	}
}
